import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { Inbox, X, ChevronsRight } from "lucide-react";
import { api } from "@/lib/api";
import { TransitionsModal } from "@/components/TransitionsModal";
import { Alert, Card, FormField, PageContainer, PageHeader, Select } from "@/components/ui";

// ============================================================================
// Bandeja «Mis solicitudes asignadas» (RF-03 §6): solicitudes de cualquier
// sector cuya etapa ACTUAL está asignada al usuario autenticado.
// ============================================================================

interface BandejaItem {
  instanciaId: string;
  recursoTipo: string;
  recursoId: string;
  folio: string;
  empresa: string;
  servicioCodigo: string;
  servicioNombre: string;
  sectorCodigo: string;
  sectorNombre: string;
  estadoCodigo: string;
  estadoNombre: string;
  fechaIngreso: string;
  fechaAsignacion: string;
}

/** Ruta del detalle según el tipo de recurso (espejo de AsignacionService.enlacePath). */
function detallePath(item: BandejaItem): string | null {
  switch (item.recursoTipo) {
    case "SOLICITUD_TELECOM": return `/sector/telecom/solicitudes/${item.recursoId}`;
    case "SOLICITUD_SEGURIDAD": return `/sector/seguridad/solicitudes/${item.recursoId}`;
    default: return null;
  }
}

/** Código de sector para TransitionsModal según el tipo de recurso. */
function sectorDeModal(item: BandejaItem): "telecom" | "seguridad" | null {
  switch (item.recursoTipo) {
    case "SOLICITUD_TELECOM": return "telecom";
    case "SOLICITUD_SEGURIDAD": return "seguridad";
    default: return null;
  }
}

function formatFecha(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" });
}

/** Tiempo transcurrido en días desde la fecha dada, legible. */
function transcurrido(iso: string | null): string {
  if (!iso) return "—";
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000);
  if (dias <= 0) return "hoy";
  if (dias === 1) return "1 día";
  return `${dias} días`;
}

export function MisAsignadasPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [sector, setSector] = useState("");
  const [estado, setEstado] = useState("");
  const [avanzar, setAvanzar] = useState<BandejaItem | null>(null);

  const q = useQuery({
    queryKey: ["mis-asignadas"],
    queryFn: async () => (await api.get<BandejaItem[]>("/api/asignaciones/mis-pendientes")).data,
    refetchInterval: 60_000,
  });

  const items = q.data ?? [];
  const sectores = useMemo(
    () => [...new Set(items.map((i) => i.sectorNombre).filter(Boolean))].sort(),
    [items],
  );
  const estados = useMemo(
    () => [...new Set(items.map((i) => i.estadoNombre).filter(Boolean))].sort(),
    [items],
  );
  const filtradas = items.filter(
    (i) => (!sector || i.sectorNombre === sector) && (!estado || i.estadoNombre === estado),
  );

  return (
    <PageContainer width="full">
      <PageHeader
        icon={<Inbox className="h-5 w-5" />}
        title="Mis solicitudes asignadas"
        subtitle="Solicitudes bajo tu responsabilidad en su etapa actual, de todos los sectores."
      />

      <Card padding="sm">
        <div className="flex flex-wrap items-end gap-3">
          <FormField label="Sector">
            <Select value={sector} onChange={(e) => setSector(e.target.value)}>
              <option value="">Todos</option>
              {sectores.map((s) => (<option key={s} value={s}>{s}</option>))}
            </Select>
          </FormField>
          <FormField label="Estatus">
            <Select value={estado} onChange={(e) => setEstado(e.target.value)}>
              <option value="">Todos</option>
              {estados.map((s) => (<option key={s} value={s}>{s}</option>))}
            </Select>
          </FormField>
          {(sector || estado) && (
            <button
              type="button"
              onClick={() => { setSector(""); setEstado(""); }}
              className="inline-flex items-center gap-1 rounded border border-edge px-3 py-2 text-sm text-fg-muted transition hover:bg-surface-2"
            >
              <X className="h-3.5 w-3.5" /> Limpiar
            </button>
          )}
        </div>
      </Card>

      <Card padding="none">
        <div className="border-b border-edge/60 px-4 py-2.5 text-xs uppercase tracking-wide text-fg-subtle">
          {filtradas.length === 0
            ? "Sin solicitudes asignadas"
            : `${filtradas.length} solicitud${filtradas.length === 1 ? "" : "es"} bajo tu responsabilidad`}
        </div>

        {q.isLoading ? (
          <div className="p-6 text-sm text-fg-subtle">Cargando…</div>
        ) : q.error ? (
          <div className="p-4"><Alert variant="warning">Error cargando tu bandeja.</Alert></div>
        ) : filtradas.length === 0 ? (
          <div className="p-8 text-center text-sm text-fg-subtle">
            No tienes solicitudes asignadas pendientes. Cuando una solicitud entre a una etapa
            bajo tu responsabilidad, aparecerá aquí.
          </div>
        ) : (
          <div className="w-full overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-surface-2">
                <tr>
                  <Th>Folio</Th>
                  <Th>Empresa</Th>
                  <Th>Servicio</Th>
                  <Th>Sector</Th>
                  <Th>Estatus</Th>
                  <Th>Ingreso</Th>
                  <Th>Asignada</Th>
                  <Th>Tiempo transcurrido</Th>
                  <Th>Acciones</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-edge bg-surface">
                {filtradas.map((r) => {
                  const path = detallePath(r);
                  return (
                    <tr
                      key={`${r.instanciaId}-${r.estadoCodigo}`}
                      className={path ? "cursor-pointer transition hover:bg-primary-tint" : undefined}
                      onClick={() => path && navigate(path)}
                    >
                      <td className="px-4 py-3 align-top font-mono text-xs font-medium">{r.folio}</td>
                      <td className="px-4 py-3 align-top font-medium">{r.empresa || "—"}</td>
                      <td className="px-4 py-3 align-top text-xs">{r.servicioNombre || r.servicioCodigo || "—"}</td>
                      <td className="px-4 py-3 align-top text-xs">{r.sectorNombre || "—"}</td>
                      <td className="px-4 py-3 align-top">
                        <span className="inline-flex items-center rounded-full bg-estado-info-bg px-2 py-0.5 text-xs font-medium text-estado-info">
                          {r.estadoNombre || r.estadoCodigo}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-top text-xs">{formatFecha(r.fechaIngreso)}</td>
                      <td className="px-4 py-3 align-top text-xs">{formatFecha(r.fechaAsignacion)}</td>
                      <td className="px-4 py-3 align-top text-xs">{transcurrido(r.fechaAsignacion)}</td>
                      <td className="px-4 py-2 align-top">
                        {sectorDeModal(r) ? (
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setAvanzar(r); }}
                            className="inline-flex items-center gap-1 rounded border border-estado-info/25 px-2 py-1 text-xs text-estado-info transition hover:bg-estado-info-bg"
                          >
                            <ChevronsRight className="h-3 w-3" /> Avanzar
                          </button>
                        ) : (
                          <span className="text-xs text-fg-faint">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {avanzar && sectorDeModal(avanzar) && (
        <TransitionsModal
          open={!!avanzar}
          solicitudId={avanzar.recursoId}
          instanciaId={avanzar.instanciaId}
          folio={avanzar.folio}
          sector={sectorDeModal(avanzar)!}
          onClose={() => setAvanzar(null)}
          onDone={async () => {
            setAvanzar(null);
            await qc.invalidateQueries({ queryKey: ["mis-asignadas"] });
          }}
        />
      )}
    </PageContainer>
  );
}

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th className="px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-fg-subtle">
      {children}
    </th>
  );
}
